/**
 * Formats a distance in meters to a human-readable scale line label.
 *
 * The algorithm selects a round number for map scale lines by choosing
 * values that follow the pattern: 1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, etc.
 * This ensures scale lines are easy to read and understand at any zoom level.
 *
 * @param maxDistanceInMeters - The maximum distance available for the scale line
 * @returns An object containing the actual distance in meters and formatted label
 *
 * @example
 * formatScaleLineDistance(3456) // returns { valueMeters: 2000, label: "2.0 km" }
 * formatScaleLineDistance(456)  // returns { valueMeters: 200, label: "200 m" }
 * formatScaleLineDistance(56)   // returns { valueMeters: 50, label: "50 m" }
 */
export function formatScaleLineDistance(
  maxDistanceInMeters: number,
): { valueMeters: number; label: string } {
  // Calculate the order of magnitude (e.g., 1234 → 1000, 56 → 10)
  const orderOfMagnitude = 10 ** Math.floor(Math.log10(maxDistanceInMeters));

  // Standard scale intervals: 1×, 2×, or 5× the order of magnitude
  const standardIntervals = [1, 2, 5];

  // Create candidate values by multiplying each interval by the order of magnitude
  // e.g., for orderOfMagnitude=100: [100, 200, 500]
  const candidateDistances = standardIntervals.map(interval => interval * orderOfMagnitude);

  // Find the largest candidate that fits within our maximum distance
  // If none fit, fall back to half the order of magnitude
  const selectedDistance = candidateDistances
    .reverse()
    .find(candidate => candidate <= maxDistanceInMeters)
    ?? orderOfMagnitude / 2;

  // Format as kilometers if 1000m or more, otherwise meters
  if (selectedDistance >= 1000) {
    const distanceInKm = selectedDistance / 1000;
    const formattedKm = distanceInKm >= 10 ? distanceInKm.toFixed(0) : distanceInKm.toFixed(1);
    return { valueMeters: selectedDistance, label: `${formattedKm} km` };
  }

  // Format as whole meters
  return { valueMeters: selectedDistance, label: `${Math.round(selectedDistance)} m` };
}
